import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OpCollapse } from 'components/customAntd/DLS/OpCollapse/OpCollapse';
import dayjs from 'dayjs';
import { DatePicker, Button, notification } from 'antd';
import type { DatePickerProps, RangePickerProps } from 'antd/es/date-picker';
import { OpCol } from './DLS/OpCol/OpCol';
import { OpRow } from './DLS/OpRow/OpRow';

interface DateRangeFilterProps {
    onDateRangeFilter: (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => void;
    initialStartDate: dayjs.Dayjs;
    initialEndDate: dayjs.Dayjs;
}

const { RangePicker } = DatePicker;

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ onDateRangeFilter, initialStartDate, initialEndDate }) => {
    const { t } = useTranslation();
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([initialStartDate, initialEndDate]);

    const handleUpdate = () => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            onDateRangeFilter(dateRange[0].startOf('day'), dateRange[1].endOf('day'));
            notification.success({
                message: t('notification.success'),
                description: t('dateRangeFilter.success'),
                placement: 'bottomRight'
            });
        }
    };

    const disabledByMonth: DatePickerProps['disabledDate'] = (current, { from }) => {
        if (from) return current.isAfter(from.add(1, 'month'));
        return false;
    };

    const rangePresets: RangePickerProps['presets'] = [
        { label: t('dateRangeFilter.thisWeek'), value: [dayjs().startOf('week'), dayjs().endOf('week')] },
        { label: t('dateRangeFilter.nextWeek'), value: [dayjs().add(1, 'week').startOf('week'), dayjs().add(1, 'week').endOf('week')] },
        { label: t('dateRangeFilter.lastWeek'), value: [dayjs().subtract(1, 'week').startOf('week'), dayjs().subtract(1, 'week').endOf('week')] },
        { label: t('dateRangeFilter.thisMonth'), value: [dayjs().startOf('month'), dayjs().endOf('month')] },
        { label: t('dateRangeFilter.lastMonth'), value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
    ];

    return (
        <OpCollapse defaultActiveKey={['1']} items={[{
            key: '1',
            label: t('dateRangeFilter.label'),
            children: (
                <OpRow gutter={[16, 16]} align="middle" style={{ width: '100%' }}>
                    <OpCol xs={24} sm={24} md={20} lg={20}>
                        <RangePicker
                            style={{ width: '100%' }}
                            value={dateRange as any}
                            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                            presets={rangePresets}
                            disabledDate={disabledByMonth}
                        />
                    </OpCol>
                    <OpCol xs={24} sm={24} md={4} lg={4}>
                        <Button type="primary" style={{ width: '100%' }} onClick={handleUpdate}>
                            {t('dateRangeFilter.update')}
                        </Button>
                    </OpCol>
                </OpRow>
            )
        }]} />
    );
};

export default DateRangeFilter;
